import type { PostgrestError, SupabaseClient } from '@supabase/supabase-js';
import { getSupabaseClient } from './supabase';

export type AuditLogRow = {
  id: number;
  table_name: string;
  record_id: string;
  action: string;
  changed_at: string;
  changed_by: string | null;
  old_data: any;
  new_data: any;
  payload_mode?: 'full' | 'compact';
  compacted_at?: string | null;
  record_label?: string | null;
  activity_source?: 'user' | 'system' | 'ai' | 'import';
};

type ClientOption = { supabase?: SupabaseClient };

export async function fetchAuditLog(limit = 150, options?: ClientOption): Promise<{ data: AuditLogRow[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('audit_log')
    .select('id,table_name,record_id,action,changed_at,changed_by,old_data,new_data,payload_mode,compacted_at')
    .order('changed_at', { ascending: false })
    .limit(limit);
  return { data: Array.isArray(data) ? (data as AuditLogRow[]) : [], error: error ?? null };
}

const AUDIT_COLUMNS = 'id,table_name,record_id,action,changed_at,changed_by,old_data,new_data';

export type VenueHistoryCursor = { changedAt: string; id: number };
export type VenueHistoryFilters = { search: string; table: string; action: string };

export type RecordHistoryCursor = VenueHistoryCursor;
export type RecordHistoryFilters = VenueHistoryFilters;

/** Stable membership is resolved by the database, including compact header edits. */
export async function fetchAuditLogForRecord(
  entityKind: 'client' | 'event' | 'installation' | 'purchase_order' | 'transfer',
  entityId: string,
  limit = 100,
  options?: ClientOption & { cursor?: RecordHistoryCursor; filters?: RecordHistoryFilters },
): Promise<{ data: AuditLogRow[]; nextCursor: RecordHistoryCursor | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const pageSize = Number.isFinite(limit) ? Math.max(1, Math.min(500, Math.trunc(limit))) : 100;
  const { data, error } = await supabase.rpc(entityKind === 'installation' ? 'installation_history_page' : 'record_history_page', {
    ...(entityKind === 'installation' ? { p_installation_job_id: entityId } : { p_entity_kind: entityKind, p_entity_id: entityId }),
    p_limit: pageSize + 1,
    p_before_at: options?.cursor?.changedAt ?? null, p_before_id: options?.cursor?.id ?? null,
    p_search: options?.filters?.search.trim() || null,
    p_table: options?.filters?.table || null, p_action: options?.filters?.action || null,
  });
  if (error) return { data: [], nextCursor: null, error };
  const rows = Array.isArray(data) ? data as AuditLogRow[] : [];
  const page = rows.slice(0, pageSize);
  const last = page[page.length - 1];
  return { data: page, error: null, nextCursor: rows.length > pageSize && last
    ? { changedAt: last.changed_at, id: last.id } : null };
}

/** The database scopes and filters before applying the keyset page limit. */
export async function fetchAuditLogForVenue(
  venueId: string,
  limit = 100,
  options?: ClientOption & { cursor?: VenueHistoryCursor; filters?: VenueHistoryFilters },
): Promise<{ data: AuditLogRow[]; nextCursor: VenueHistoryCursor | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const pageSize = Number.isFinite(limit) ? Math.max(1, Math.min(500, Math.trunc(limit))) : 100;
  const { data, error } = await supabase.rpc('venue_history_page', {
    p_venue_id: venueId,
    p_limit: pageSize + 1,
    p_before_at: options?.cursor?.changedAt ?? null,
    p_before_id: options?.cursor?.id ?? null,
    p_search: options?.filters?.search.trim() || null,
    p_table: options?.filters?.table || null,
    p_action: options?.filters?.action || null,
  });
  if (error) return { data: [], nextCursor: null, error };
  const rows = Array.isArray(data) ? data as AuditLogRow[] : [];
  const page = rows.slice(0, pageSize);
  const last = page[page.length - 1];
  return {
    data: page,
    // Preserve PostgreSQL timestamp precision; Date would truncate microseconds.
    nextCursor: rows.length > pageSize && last ? { changedAt: last.changed_at, id: last.id } : null,
    error: null,
  };
}

export function auditActorLabel(row: AuditLogRow, names: Record<string, string>): string {
  const source = row.activity_source ?? (row.table_name === 'venue_research_runs' ? 'ai' : row.changed_by ? 'user' : 'system');
  const actor = row.changed_by ? names[row.changed_by] || row.changed_by.slice(0, 8) : null;
  if (source === 'ai') return actor ? `${actor} (AI research)` : 'AI research';
  if (source === 'import') return actor ? `${actor} (Import)` : 'Import';
  return actor || 'System';
}

export async function undoAuditChange(auditId: number, options?: ClientOption): Promise<PostgrestError | null> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { error } = await supabase.rpc('undo_change', { audit_id: auditId });
  return error ?? null;
}

export function isAuditRowUndoable(row: AuditLogRow): boolean {
  // The legacy restore RPC addresses rows by `id`; milestones use milestone_id.
  // Keep them editable in Schedule, without offering a restore that cannot run.
  return (row.payload_mode ?? 'full') === 'full' && row.table_name !== 'event_schedule_milestones';
}
